/* Beauty By Seoul — Theme JS */
'use strict';

// ============================================================
// FADE-UP SCROLL ANIMATIONS
// ============================================================
function initScrollAnimations() {
  const els = document.querySelectorAll('.fade-up');
  if (!els.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  els.forEach(el => observer.observe(el));
}

// ============================================================
// CART DRAWER
// ============================================================
const FREE_SHIPPING_THRESHOLD = 6500; // $65.00 in cents

// Contextual cart upsell: first rule whose trigger is in the cart AND whose
// offer is NOT already in the cart wins. No match = no upsell shown.
const UPSELL_RULES = [
  { trigger: 'waxroller',  offer: 'waxrefill'  },
  { trigger: 'sunstickt2', offer: 'glowserum'  },
  { trigger: 'claystick',  offer: 'glowserum'  },
];
const UPSELL_COPY = {
  waxrefill: { sub: '4 blocks · ~8 sessions · Strip-free', reviews: '2,847 reviews' },
  glowserum: { sub: 'Pairs with your SPF — rice water + alpha-arbutin to fade dark spots and boost glow.', reviews: '1,743 reviews' },
};

const CartDrawer = {
  drawer: null,

  init() {
    this.drawer = document.getElementById('cart-drawer');
    if (!this.drawer) return;

    document.querySelector('.cart-drawer__overlay')?.addEventListener('click', () => this.close());
    document.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());

    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });

  },

  // Cache of fetched product JSON, keyed by handle
  _upsellCache: {},
  async fetchUpsellProduct(handle) {
    if (this._upsellCache[handle]) return this._upsellCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const product = await res.json();
      this._upsellCache[handle] = product;
      return product;
    } catch(e) { return null; }
  },

  open() {
    this.drawer.classList.add('open');
    // Lock scroll — works on iOS Safari too
    this._scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.width = '100%';
    this.fetchCart();
  },

  close() {
    this.drawer.classList.remove('open');
    // Restore scroll position
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this._scrollY || 0);
  },

  async fetchCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch(e) {}
  },

  render(cart) {
    const itemsEl = document.getElementById('cart-items');
    const subtotalEl = document.getElementById('cart-subtotal');
    const countEl = document.getElementById('drawer-item-count');
    if (!itemsEl) return;

    // Update item count badge
    if (countEl) countEl.textContent = cart.item_count;

    // Update shipping progress bar
    this.renderShippingBar(cart.total_price);

    // Empty state
    if (!cart.items.length) {
      itemsEl.innerHTML = `
        <div style="text-align:center;padding:48px 24px">
          <div style="font-size:48px;margin-bottom:16px">🛍️</div>
          <p class="cart-empty">Your cart is empty</p>
          <a href="/collections/products" class="btn btn--primary" style="margin-top:20px" onclick="CartDrawer.close()">Start Shopping</a>
        </div>`;
      if (subtotalEl) subtotalEl.textContent = '$0.00';
      document.getElementById('cart-upsell').style.display = 'none';
      return;
    }

    // Render items — display oldest-added first so upsells added later appear
    // BELOW the products already in the cart. Shopify's /cart.js returns items
    // newest-first, so we reverse for display while preserving each item's
    // ORIGINAL 1-based line number (qty/remove calls target cart lines, not
    // display positions).
    const displayItems = cart.items.map((item, index) => ({ item, line: index + 1 })).reverse();
    itemsEl.innerHTML = displayItems.map(({ item, line }) => `
      <div class="cart-item" data-index="${line}">
        <a href="${item.url}" onclick="CartDrawer.close()">
          <img src="${item.image}" alt="${item.title}" class="cart-item__img">
        </a>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__title" onclick="CartDrawer.close()">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${this.formatMoney(item.final_line_price)}</div>
          <div class="cart-item__controls">
            <div class="cart-item__qty">
              <button class="cart-item__qty-btn" onclick="CartDrawer.updateQty(${line}, ${item.quantity - 1})">−</button>
              <span class="cart-item__qty-num">${item.quantity}</span>
              <button class="cart-item__qty-btn" onclick="CartDrawer.updateQty(${line}, ${item.quantity + 1})">+</button>
            </div>
            <button class="cart-item__remove" onclick="CartDrawer.updateQty(${line}, 0)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="3,6 5,6 21,6"/><path d="M19,6l-1,14H6L5,6"/><path d="M10,11v6M14,11v6"/><path d="M9,6V4h6v2"/></svg>
              Remove
            </button>
          </div>
        </div>
      </div>
    `).join('');

    if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);

    // Contextual upsell: pick the first matching rule
    this.renderUpsell(cart);
  },

  async renderUpsell(cart) {
    const upsellEl = document.getElementById('cart-upsell');
    if (!upsellEl) return;

    const handlesInCart = cart.items.map(i => i.handle);
    const rule = UPSELL_RULES.find(r =>
      handlesInCart.includes(r.trigger) && !handlesInCart.includes(r.offer)
    );

    if (!rule) { upsellEl.style.display = 'none'; return; }

    const product = await this.fetchUpsellProduct(rule.offer);
    if (!product || !product.available) { upsellEl.style.display = 'none'; return; }

    // Populate the card
    const copy = UPSELL_COPY[rule.offer] || {};
    const img = document.getElementById('upsell-img');
    if (img) {
      img.src = product.featured_image ? product.featured_image + (product.featured_image.includes('?') ? '&' : '?') + 'width=200' : '';
      img.alt = product.title;
    }
    const nameEl = document.getElementById('upsell-name');
    if (nameEl) nameEl.textContent = product.title;
    const subEl = document.getElementById('upsell-sub');
    if (subEl) subEl.textContent = copy.sub || '';
    const starsEl = document.getElementById('upsell-stars');
    if (starsEl) starsEl.textContent = copy.reviews || '';
    const priceEl = document.getElementById('upsell-price');
    if (priceEl) priceEl.textContent = this.formatMoney(product.price);

    const upsellBtn = document.getElementById('upsell-add-btn');
    if (upsellBtn) {
      upsellBtn.dataset.variantId = product.variants[0]?.id || '';
      if (!upsellBtn._bound) {
        upsellBtn._bound = true;
        upsellBtn.addEventListener('click', async () => {
          const vid = upsellBtn.dataset.variantId;
          if (!vid) return;
          upsellBtn.textContent = 'Adding...';
          upsellBtn.disabled = true;
          await addToCart(vid, 1, false); // false = don't re-open drawer
          await this.fetchCart();
          upsellBtn.textContent = '✓ Added!';
          setTimeout(() => {
            upsellBtn.textContent = '+ Add to Cart';
            upsellBtn.disabled = false;
          }, 2000);
        });
      }
    }

    upsellEl.style.display = 'block';
  },

  renderShippingBar(totalCents) {
    const barText = document.getElementById('shipping-bar-text');
    const barFill = document.getElementById('shipping-bar-fill');
    if (!barText || !barFill) return;

    const remaining = FREE_SHIPPING_THRESHOLD - totalCents;
    const pct = Math.min((totalCents / FREE_SHIPPING_THRESHOLD) * 100, 100);
    barFill.style.width = pct + '%';

    if (remaining <= 0) {
      barText.innerHTML = `🎉 <strong>You've unlocked free shipping!</strong>`;
      barFill.style.background = '#16a34a';
    } else {
      barText.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> more for <strong>free shipping</strong>`;
      barFill.style.background = 'var(--color-purple)';
    }
  },

  async updateQty(line, qty) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: qty })
      });
      await this.fetchCart();
      updateCartCount();
    } catch(e) {}
  },

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
};

// ============================================================
// ADD TO CART
// ============================================================
async function addToCart(variantId, qty = 1, openDrawer = true) {
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error('Add to cart failed');
    if (openDrawer) CartDrawer.open();
    updateCartCount();
  } catch(e) {
    console.error('Add to cart error:', e);
  }
}

async function updateCartCount() {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    const countEl = document.querySelector('.cart-count');
    if (countEl) countEl.textContent = cart.item_count;
  } catch(e) {}
}

// ============================================================
// QTY CONTROLS
// ============================================================
function initQtyControls() {
  document.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('.qty-control')?.querySelector('.qty-input');
      if (!input) return;
      let val = parseInt(input.value) || 1;
      if (btn.dataset.dir === 'up') val = Math.min(val + 1, 10);
      if (btn.dataset.dir === 'down') val = Math.max(val - 1, 1);
      input.value = val;
    });
  });
}

// ============================================================
// PRODUCT FORM
// ============================================================
function initProductForms() {
  // Explicitly exclude the sticky ATC button — it has its own handler in initStickyATC()
  document.querySelectorAll('.js-add-to-cart:not(.sticky-atc__btn)').forEach(btn => {
    const originalText = btn.textContent.trim();
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const form = btn.closest('form');
      const variantInput = form?.querySelector('[name="id"]') || document.querySelector('[name="id"]');
      const variantId = variantInput?.value;
      const qty = parseInt(form?.querySelector('[name="quantity"]')?.value, 10) || 1;

      if (!variantId) return;

      btn.textContent = 'Adding...';
      btn.disabled = true;

      await addToCart(variantId, qty);

      btn.textContent = 'Added ✓';
      setTimeout(() => {
        btn.textContent = btn.dataset.baseText || originalText;
        btn.disabled = false;
      }, 2000);
    });
  });
}

// ============================================================
// PRODUCT IMAGE THUMBS
// ============================================================
function initProductThumbs() {
  const thumbs = document.querySelectorAll('.product-thumb');
  const mainImg = document.querySelector('.product-main-img img');
  const track = document.getElementById('product-thumbs-track');
  const prevBtn = document.getElementById('thumbs-prev');
  const nextBtn = document.getElementById('thumbs-next');

  if (!thumbs.length || !mainImg) return;

  // Thumb click — swap main image
  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      const fullSrc = thumb.dataset.src;
      if (!fullSrc) return;

      mainImg.style.opacity = '0';
      mainImg.style.transition = 'opacity 0.15s ease';

      const newImg = new Image();
      newImg.onload = () => {
        mainImg.src = fullSrc;
        mainImg.style.opacity = '1';
      };
      newImg.src = fullSrc;

      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });

  // Arrow buttons
  if (!track || !prevBtn || !nextBtn) return;

  const scrollAmount = 160; // px per click

  prevBtn.addEventListener('click', () => {
    track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  });

  nextBtn.addEventListener('click', () => {
    track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  });

  // Show/hide arrows based on scroll position
  function updateArrows() {
    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    prevBtn.classList.toggle('hidden', atStart);
    nextBtn.classList.toggle('hidden', atEnd);
  }

  track.addEventListener('scroll', updateArrows, { passive: true });

  // Initial state — hide prev, hide next if all thumbs fit
  updateArrows();

  // Re-check after images load
  window.addEventListener('load', updateArrows);
}

// ============================================================
// MOBILE MENU
// ============================================================
function initMobileMenu() {
  const btn = document.querySelector('.mobile-menu-btn');
  const nav = document.getElementById('mobile-nav');
  const overlay = document.getElementById('mobile-nav-overlay');
  const closeBtn = document.getElementById('mobile-nav-close');
  if (!btn || !nav) return;

  function openMenu() {
    nav.classList.add('open');
    overlay?.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu() {
    nav.classList.remove('open');
    overlay?.classList.remove('open');
    document.body.style.overflow = '';
  }

  btn.addEventListener('click', openMenu);
  closeBtn?.addEventListener('click', closeMenu);
  overlay?.addEventListener('click', closeMenu);

  nav.querySelectorAll('a').forEach(a => {
    a.addEventListener('click', closeMenu);
  });
}

// ============================================================
// HEADER SCROLL STATE
// ============================================================
function initHeaderScroll() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const scroll = window.scrollY;
    if (scroll > 80) {
      header.style.boxShadow = '0 4px 40px rgba(0,0,0,0.5)';
    } else {
      header.style.boxShadow = 'none';
    }
    lastScroll = scroll;
  }, { passive: true });
}

// ============================================================
// INIT
// ============================================================
document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  CartDrawer.init();
  initQtyControls();
  initProductForms();
  initProductThumbs();
  initMobileMenu();
  initHeaderScroll();
  updateCartCount();
  initStickyATC();
});

// ============================================================
// STICKY ATC BAR — shows after main ATC button scrolls out of view
// ============================================================
function initStickyATC() {
  const bar = document.getElementById('sticky-atc');
  // Target the product-form submit button directly — works regardless of DOM nesting
  const mainBtn = document.querySelector('#product-form [type="submit"]');
  if (!bar || !mainBtn) return;

  // Only show after the button has been scrolled past downward
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Button is visible — always hide
        bar.style.display = 'none';
      } else {
        // Button is out of view — only show if it's ABOVE the viewport (user scrolled past it)
        const rect = mainBtn.getBoundingClientRect();
        if (rect.top < 0) {
          bar.style.display = 'flex';
        } else {
          // Button is below viewport (user hasn't reached it yet) — keep hidden
          bar.style.display = 'none';
        }
      }
    });
  }, { threshold: 0 });

  observer.observe(mainBtn);

  bar.querySelector('.sticky-atc__btn')?.addEventListener('click', async (e) => {
    e.preventDefault();
    const variantInput = document.querySelector('[name="id"]');
    const variantId = variantInput?.value;
    if (!variantId) return;

    const btn = e.currentTarget;
    btn.textContent = 'Adding...';
    btn.disabled = true;
    await addToCart(variantId, 1);
    btn.textContent = 'Added \u2713';
    setTimeout(() => {
      btn.textContent = 'Add to Cart';
      btn.disabled = false;
    }, 2000);
  });
}

